import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect
} from "@tanstack/react-router";
import { lazy, Suspense, type ReactNode } from "react";

const ProtectedPage = lazy(() => import("./components/protected-page").then((module) => ({ default: module.ProtectedPage })));
const AccessSettingsPage = lazy(() => import("./pages/access-settings").then((module) => ({ default: module.AccessSettingsPage })));
const AuditLogsPage = lazy(() => import("./pages/audit-logs").then((module) => ({ default: module.AuditLogsPage })));
const ChangelogPage = lazy(() => import("./pages/changelog").then((module) => ({ default: module.ChangelogPage })));
const CompanyProfilePage = lazy(() => import("./pages/company-profile").then((module) => ({ default: module.CompanyProfilePage })));
const DashboardPage = lazy(() => import("./pages/dashboard").then((module) => ({ default: module.DashboardPage })));
const LoginPage = lazy(() => import("./pages/login").then((module) => ({ default: module.LoginPage })));
const RegistrationsPage = lazy(() => import("./pages/registrations").then((module) => ({ default: module.RegistrationsPage })));
const ReportsPage = lazy(() => import("./pages/reports").then((module) => ({ default: module.ReportsPage })));
const VehicleDetailPage = lazy(() => import("./pages/vehicle-detail").then((module) => ({ default: module.VehicleDetailPage })));
const VehicleNewPage = lazy(() => import("./pages/vehicle-new").then((module) => ({ default: module.VehicleNewPage })));
const VehiclesPage = lazy(() => import("./pages/vehicles").then((module) => ({ default: module.VehiclesPage })));

function RouteFallback() {
  return (
    <div role="status" aria-live="polite" className="flex min-h-dynamic-screen items-center justify-center text-sm text-muted-foreground">
      Carregando...
    </div>
  );
}

function PageFallback() {
  return (
    <div role="status" aria-live="polite" className="p-6 text-sm text-muted-foreground">
      Carregando tela...
    </div>
  );
}

function ProtectedRoute({ children }: { children: ReactNode }) {
  return (
    <Suspense fallback={<RouteFallback />}>
      <ProtectedPage>
        <Suspense fallback={<PageFallback />}>{children}</Suspense>
      </ProtectedPage>
    </Suspense>
  );
}

const rootRoute = createRootRoute({
  component: () => <Outlet />
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/dashboard" });
  }
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: () => (
    <Suspense fallback={<RouteFallback />}>
      <LoginPage />
    </Suspense>
  )
});

const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard",
  component: () => (
    <ProtectedRoute>
      <DashboardPage />
    </ProtectedRoute>
  )
});

const vehiclesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/vehicles",
  component: () => (
    <ProtectedRoute>
      <VehiclesPage />
    </ProtectedRoute>
  )
});

const vehicleDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/vehicles/$vehicleId",
  component: () => (
    <ProtectedRoute>
      <VehicleDetailPage />
    </ProtectedRoute>
  )
});

const vehicleNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/vehicles/new",
  component: () => (
    <ProtectedRoute>
      <VehicleNewPage />
    </ProtectedRoute>
  )
});

const financeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/finance",
  beforeLoad: () => {
    throw redirect({ to: "/vehicles" });
  }
});

const reportsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reports",
  component: () => (
    <ProtectedRoute>
      <ReportsPage />
    </ProtectedRoute>
  )
});

const registrationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/registrations",
  beforeLoad: () => {
    throw redirect({ to: "/registrations/people" });
  }
});

const registrationPeopleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/registrations/people",
  component: () => (
    <ProtectedRoute>
      <RegistrationsPage entity="people" />
    </ProtectedRoute>
  )
});

const registrationStoresRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/registrations/stores",
  component: () => (
    <ProtectedRoute>
      <RegistrationsPage entity="stores" />
    </ProtectedRoute>
  )
});

const registrationResponsiblesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/registrations/responsibles",
  component: () => (
    <ProtectedRoute>
      <RegistrationsPage entity="responsibles" />
    </ProtectedRoute>
  )
});

const registrationBrandsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/registrations/brands",
  component: () => (
    <ProtectedRoute>
      <RegistrationsPage entity="brands" />
    </ProtectedRoute>
  )
});

const registrationModelsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/registrations/models",
  component: () => (
    <ProtectedRoute>
      <RegistrationsPage entity="models" />
    </ProtectedRoute>
  )
});

const registrationColorsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/registrations/colors",
  component: () => (
    <ProtectedRoute>
      <RegistrationsPage entity="colors" />
    </ProtectedRoute>
  )
});

const registrationCurrenciesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/registrations/currencies",
  component: () => (
    <ProtectedRoute>
      <RegistrationsPage entity="currencies" />
    </ProtectedRoute>
  )
});

const registrationCompanyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/registrations/company",
  component: () => (
    <ProtectedRoute>
      <CompanyProfilePage />
    </ProtectedRoute>
  )
});

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings",
  component: () => (
    <ProtectedRoute>
      <AccessSettingsPage />
    </ProtectedRoute>
  )
});

const auditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/audit",
  component: () => (
    <ProtectedRoute>
      <AuditLogsPage />
    </ProtectedRoute>
  )
});

const changelogRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/changelog",
  component: () => (
    <ProtectedRoute>
      <ChangelogPage />
    </ProtectedRoute>
  )
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  dashboardRoute,
  vehiclesRoute,
  vehicleNewRoute,
  vehicleDetailRoute,
  financeRoute,
  reportsRoute,
  registrationsRoute,
  registrationPeopleRoute,
  registrationStoresRoute,
  registrationResponsiblesRoute,
  registrationBrandsRoute,
  registrationModelsRoute,
  registrationColorsRoute,
  registrationCurrenciesRoute,
  registrationCompanyRoute,
  settingsRoute,
  auditRoute,
  changelogRoute
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
